// Import types
import type { HoverInfo } from '../../types/tooltip';

// Import utils
import { formatColumnDate, isoUtcToHM, formatTime } from '../../utils/date';
import { getCellUtcInterval } from '../../utils/date';
import { logger } from '../../utils/logger';

// Import components
import Tooltip from '../tooltip/Tooltip';

export type HoverTooltipProps = {
  hoverInfo: HoverInfo | null;
  disabled?: boolean;
  ymdByDateKey: Map<string, string>;
  getYmd: (dateKey: string) => string | undefined;
  TIMES: string[];
  gridStart: string;
  gridEnd: string;
  hourMode: string;
  ownerTimezone?: string | null;
  timezone: string;
};

const HoverTooltip = ({
  hoverInfo,
  disabled,
  ymdByDateKey,
  getYmd,
  TIMES,
  gridStart,
  gridEnd,
  hourMode,
  ownerTimezone,
  timezone,
}: HoverTooltipProps) => {
  if (!hoverInfo || disabled) return null;

  const { dateKey, row, x, y } = hoverInfo;
  const baseTz = ownerTimezone || timezone;

  // Build the tooltip's date label by interpreting the column day in the
  // owner's timezone, then rendering in the viewer's timezone (same as header)
  let dateLabel = '';
  const ymdForDateKey = ymdByDateKey.get(dateKey);
  if (ymdForDateKey) {
    dateLabel = formatColumnDate(
      ymdForDateKey,
      'ddd, MMM D, YYYY',
      baseTz,
      timezone,
    );
  } else {
    // Fallback: format from the dateKey with the viewer's timezone
    const d = new Date(dateKey);
    dateLabel = d.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: timezone,
    });
  }

  // Compute tooltip times from the cell UTC interval using the owner/viewer basis
  const ymd = getYmd(dateKey) || ymdByDateKey.get(dateKey);
  let start = TIMES[row];
  let end = row + 1 < TIMES.length ? TIMES[row + 1] : gridEnd;
  if (ymd) {
    try {
      const iv = getCellUtcInterval(ymd, row, gridStart, gridEnd, baseTz);
      start = isoUtcToHM(iv.startIso, timezone);
      end = isoUtcToHM(iv.endIso, timezone);
    } catch (e) {
      logger.error('Failed to compute tooltip times:', e);
    }
  }

  const content = `${dateLabel} ${formatTime(start, hourMode)} to ${formatTime(end, hourMode)}`;

  return (
    <Tooltip visible x={x} y={y}>
      {content}
    </Tooltip>
  );
};

export default HoverTooltip;
